﻿<template>
  <div id="app" class="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
    <!-- Global loading spinner for entities initialization -->
    <div
      v-if="entitiesStore.loading && !entitiesStore.loaded && !isAuthPage"
      class="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-xl">
        <div class="flex flex-col items-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
          <p class="mt-4 text-gray-700 dark:text-gray-300 font-medium">Loading...</p>
        </div>
      </div>
    </div>

    <component :is="layout">
      <router-view />
    </component>
    <!-- Notification balloon + alert tone + TTS: mounted on every app-chrome
         layout (hub UI *and* the Servers page). Previously restricted to the
         hub layout, so alerts arriving while you were on the Servers page were
         silently swallowed. Not mounted on bare/connect/kiosk pages. -->
    <NotificationToast v-if="showNotificationChrome" />
    <template v-if="mountRuntimeChrome">
      <AlarmRingOverlay />
      <CameraModal />
      <FloatingCameraPlayer />
      <BrowserMediaElements />
      <FloatingBrowserVideoPlayer />
      <IntercomCallOverlay />
      <PeopleCallOverlay />
      <VoiceCommandsModal />
    </template>

    <!-- Wake-word bottom bar/overlay: shown on the hub UI and on connected-server
         (scoped) pages alike — it was historically global. Self-hides unless the
         wake word is armed/active, so it is safe to mount outside the layout. -->
    <WakeWordIndicator v-if="!useBareLayout" />

    <!-- ── Global device control modal ── -->
    <Teleport to="body">
      <DeviceControlModal
        v-if="ui.deviceControlModalDevice"
        :device="ui.deviceControlModalDevice"
        :integration-name="getIntegrationName(ui.deviceControlModalDevice?.integrationId)"
        @close="ui.closeDeviceControlModal()"
        @open-settings="openGlobalDeviceSettings"
        @view-stream="ui.closeDeviceControlModal()"
        @view-details="ui.closeDeviceControlModal()"
        @delete="ui.closeDeviceControlModal()" />
      <DeviceSettingsModal
        v-if="settingsDevice"
        :device="settingsDevice"
        @close="settingsDevice = null" />
    </Teleport>

    <!-- Desktop (Electron) auto-update banner -->
    <UpdateBanner />

    <!-- The install offer for phone browsers on app.torik.ai (the owner's ask,
         2026-10-05). Self-hides everywhere else; the rule is
         `utils/installOffer.ts`, today's doors `config/appStores.ts`. -->
    <InstallAppBanner />

    <!-- Hub tab whose session dropped: stays in the tab and offers
         Reconnect / Close instead of bouncing back to the launcher. -->
    <ConnectionLostOverlay v-if="connectionLostStore.lost" />

    <!-- Global overlays (always mounted so toasts/dialogs work on every layout) -->
    <ToastNotifications />
    <MessageBoxHost />
    <CloudAccountPanel />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch, nextTick, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useThemeStore } from './stores/theme';
import { useEntitiesStore } from './stores/entities';
import { useLocaleStore } from './stores/locale';
import { useSocketStore } from './stores/socket';
import { getRuntimeConfig, isElectron, getActiveHubEndpoint, readTabActiveHub } from './config/runtime';
import { hasStoredDeviceCredential } from './services/hubBinding';
import { mayRenderHubShell } from './utils/hubOpenFallback';
// The desktop launcher home holds no hub connection and announces nothing
// (card t_f0ea3681) — see the module.
import { mountsNotificationChrome, onDesktopLauncherHome, setSurfacePath } from './services/launcherSurface';
// `<html lang>` follows the locale store (card t_8ca38a80) — see the module.
import { installDocumentLang } from './utils/documentLang';
// When the tracker may start (card t_88c3d858) — see the module.
import { shouldStartLocationTracking } from './utils/locationTrackingGate';
import { useWebRTCStore } from './stores/webrtc';
import { useSystemStore } from './stores/system';
import { useSystemStatusStore } from './stores/systemStatus';
import { parseHubApiUrl } from './services/hubConnect';
import { useCamerasStore } from './stores/cameras';
import { useTouchPanelStore } from './stores/touchPanel';
import DefaultLayout from './layouts/DefaultLayout.vue';
import ServerLayout from './layouts/ServerLayout.vue';
// The needs-join state for a hub page this window holds no credential for
// (card `t_bfe0edc9`) — chosen by `mayRenderHubShell` below.
import HubNeedsCredential from './components/hub/HubNeedsCredential.vue';
import ToastNotifications from './components/ToastNotifications.vue';
import MessageBoxHost from './components/MessageBoxHost.vue';
import CloudAccountPanel from './components/cloud/CloudAccountPanel.vue';
import NotificationToast from './components/NotificationToast.vue';
import AlarmRingOverlay from './components/AlarmRingOverlay.vue';
import { useLocationTracking } from './composables/useLocationTracking';
import { useAlarmSiren } from './composables/useAlarmSiren';
import { useDemoGuestHeartbeat } from './composables/useDemoGuestHeartbeat';
import { useDocumentTitle } from './composables/useDocumentTitle';
import { useFavicon } from './composables/useFavicon';
import CameraModal from './components/CameraModal.vue';
import FloatingCameraPlayer from './components/FloatingCameraPlayer.vue';
import BrowserMediaElements from './components/media/BrowserMediaElements.vue';
import FloatingBrowserVideoPlayer from './components/media/FloatingBrowserVideoPlayer.vue';
import IntercomCallOverlay from './components/IntercomCallOverlay.vue';
import PeopleCallOverlay from './components/PeopleCallOverlay.vue';
import WakeWordIndicator from './components/WakeWordIndicator.vue';
import VoiceCommandsModal from './components/VoiceCommandsModal.vue';
import DeviceControlModal from './components/DeviceControlModal.vue';
import DeviceSettingsModal from './components/DeviceSettingsModalNew.vue';
import UpdateBanner from './components/UpdateBanner.vue';
import InstallAppBanner from './components/InstallAppBanner.vue';
import ConnectionLostOverlay from './components/ConnectionLostOverlay.vue';
import { useHubUpdateNotifier } from './composables/useHubUpdateNotifier';
import { useConnectionLostWatcher } from './composables/useConnectionLost';
import { useUIStore } from './stores/ui';
import { useConnectionLostStore } from './stores/connectionLost';
import { initOAuthDeepLinks } from './services/capacitor-oauth';
import { initGlobalOAuthListener } from './services/oauth-handler';
import { installMobileBackNavigation, installMobileEdgeSwipe, useMobileBack } from './composables/useMobileBack';
import { forgetNativePush, initNativePush, forgetWebPush, subscribeWebPush } from './services/pushNotifications';
import { initBackgroundGeofenceSync } from './services/backgroundGeofence';
import { openHubFromPush } from './services/pushDeepLink';
import { isPushArrival } from './services/pushTarget';
import type { Device } from '@/types';

const authStore = useAuthStore();
const themeStore = useThemeStore();
const entitiesStore = useEntitiesStore();
const localeStore = useLocaleStore();
const touchPanelStore = useTouchPanelStore();
const route = useRoute();
const router = useRouter();
const locationTracking = useLocationTracking();
const ui = useUIStore();
/** The settings sheet the global control sheet's gear swaps to — see
 *  `openGlobalDeviceSettings` below. */
const settingsDevice = ref<Device | null>(null);
const connectionLostStore = useConnectionLostStore();

// The document's own language: `<html lang>` is what a screen reader, the
// browser's translate offer, hyphenation and spell-check read — none of them
// see the locale store. Watches the store, so the boot path in `onMounted`
// below and the account preferences' language picker both move it, with no
// reload (card t_8ca38a80).
installDocumentLang();

// Hub stops answering (sustained socket drop) → "connection lost" overlay.
useConnectionLostWatcher();

// Phone chrome (card t_6a634e11): hand the back/hardware-back/swipe handlers the
// router. This has to happen in setup — `useRouter()` resolves nothing once the
// component is mounted, and a late call would blank the wiring the header
// components install. The listeners themselves go up in `onMounted` below.
useMobileBack();

// Global security alarm siren: 10s blare, repeated every minute while active.
useAlarmSiren();

// The guest's liveness ping (card `t_6d25f68a`, seam 3): D9's guest TTL is
// inactivity-based and nothing in the app called `POST /api/demo/session/
// heartbeat`, so an actively used guest session died 2 h after it was created.
// The composable runs the loop while a guest is in front of this screen.
useDemoGuestHeartbeat();

// Dynamic browser tab title based on system name
useDocumentTitle();

/** Module-level timer so the scheduler can be stopped/re-started on auth changes. */
let cloudRefreshTimer: number | null = null;

function stopCloudTokenRefresh(): void {
  if (cloudRefreshTimer !== null) {
    window.clearTimeout(cloudRefreshTimer);
    cloudRefreshTimer = null;
  }
}

/**
 * Proactively refresh the cloud access token ~5 minutes before it expires.
 * On refresh failure it retries once, then invalidates the stale cloud session
 * so the user is prompted to sign in again (instead of silently breaking
 * cloud features). Restarts itself after each successful refresh.
 */
function scheduleCloudTokenRefresh(): void {
  if (!authStore.isCloudAuthenticated || !authStore.cloudAccessToken) return;

  let failedAttempts = 0;

  const handleRefreshResult = (refreshed: boolean): void => {
    if (refreshed) {
      failedAttempts = 0;
      schedule(); // Schedule the next refresh
      return;
    }
    // Refresh failed — retry shortly (network blip), then invalidate.
    failedAttempts += 1;
    if (failedAttempts >= 2) {
      authStore.invalidateCloudSession();
      return;
    }
    cloudRefreshTimer = window.setTimeout(() => {
      authStore.refreshCloudAccessToken().then(handleRefreshResult);
    }, 15_000);
  };

  const schedule = () => {
    if (!authStore.cloudAccessToken) return;
    try {
      const payload = JSON.parse(atob(authStore.cloudAccessToken.split('.')[1]));
      const expiresAt = payload.exp * 1000; // JWT exp is in seconds
      const refreshAt = expiresAt - 5 * 60 * 1000; // 5 min before expiry
      const delay = Math.max(0, refreshAt - Date.now());

      if (delay > 0) {
        console.log(`[App] Scheduling cloud token refresh in ${Math.round(delay / 60000)} min`);
        cloudRefreshTimer = window.setTimeout(async () => {
          const refreshed = await authStore.refreshCloudAccessToken();
          handleRefreshResult(refreshed);
        }, delay);
      } else {
        // Already expired or about to — refresh now.
        authStore.refreshCloudAccessToken().then(handleRefreshResult);
      }
    } catch {
      // Can't decode — try refreshing anyway.
      authStore.refreshCloudAccessToken().then(handleRefreshResult);
    }
  };

  schedule();
}

// (Re)arm the proactive cloud refresh whenever cloud auth changes — signing in
// after app start must also start the scheduler, and logout must stop it.
watch(
  () => authStore.isCloudAuthenticated,
  (authenticated) => {
    if (authenticated) {
      scheduleCloudTokenRefresh();
      // Native builds register their FCM token against the signed-in account;
      // signing out hands the device back (no pushes for the previous user).
      void initNativePush();
      // A browser build subscribes to Web Push the same way. It answers
      // `unsupported` in the packaged app and in Electron — neither has a
      // service worker to hold a subscription — so the two paths sit side by
      // side without either having to know which build it is in.
      void subscribeWebPush().then((outcome) => {
        if (!outcome.ok && outcome.reason !== 'unsupported') {
          console.info(`[Push] browser push not subscribed (${outcome.reason})`);
        }
      });
    } else {
      stopCloudTokenRefresh();
      void forgetNativePush();
      void forgetWebPush();
    }
  },
  { immediate: true },
);

// Background geofences (card t_23f46db0, plan §2.4): the OS's registered regions
// follow the same two gates the cloud location report does — a signed-in account
// and the recorded opt-in — so they are installed beside the push registration
// above, which watches exactly those. Every path inside is a no-op off Android.
void initBackgroundGeofenceSync();

let localRefreshTimer: number | null = null;

/**
 * Proactively refresh the local hub token ~5 minutes before it expires.
 * The hub issues a 24h JWT without a refresh token, so the sliding-session
 * endpoint is called while the token is still valid. Without this, the first
 * failure shows up as a device control error ("Invalid token") after ~24h.
 */
function scheduleLocalTokenRefresh(): void {
  const config = getRuntimeConfig();
  if (config.mode === 'cloud') return;
  if (localRefreshTimer !== null) {
    window.clearTimeout(localRefreshTimer);
    localRefreshTimer = null;
  }

  const schedule = () => {
    const tokenValue = authStore.accessToken;
    if (!tokenValue) return;
    try {
      const payload = JSON.parse(atob(tokenValue.split('.')[1]));
      const expiresAt = payload.exp * 1000; // JWT exp is in seconds
      const refreshAt = expiresAt - 5 * 60 * 1000; // 5 min before expiry
      const delay = Math.max(0, refreshAt - Date.now());

      if (delay > 0) {
        console.log(`[App] Scheduling local hub token refresh in ${Math.round(delay / 60000)} min`);
        localRefreshTimer = window.setTimeout(async () => {
          const refreshed = await authStore.refreshLocalHubToken();
          if (refreshed) {
            schedule(); // Schedule next refresh
          }
        }, delay);
      } else {
        // Already expired or about to — refresh now
        authStore.refreshLocalHubToken();
      }
    } catch {
      // Can't decode — skip; the API layer recovers via its 401 handler.
    }
  };

  schedule();
}

// (Re)schedule the local hub token refresh whenever a hub token appears
// (app start, login later in the session) and cancel it on logout.
watch(
  () => authStore.accessToken,
  (newToken) => {
    if (newToken) {
      scheduleLocalTokenRefresh();
    } else if (localRefreshTimer !== null) {
      window.clearTimeout(localRefreshTimer);
      localRefreshTimer = null;
    }
  },
);

// Dynamic favicon management
const favicon = useFavicon();

/**
 * The global control sheet's gear (what the lights widget opens): swap it for
 * the settings sheet instead of only closing — the card surfaces' own pattern
 * (`DeviceIconCard.vue`). The gear emits `open-settings` before `close`, and
 * the close handler only closes the control modal, so the captured device
 * survives into the settings sheet.
 */
function openGlobalDeviceSettings(): void {
  const device = ui.deviceControlModalDevice;
  ui.closeDeviceControlModal();
  settingsDevice.value = device ?? null;
}

function getIntegrationName(integrationId?: string): string {
  if (!integrationId) return '';
  const integration = entitiesStore.integrations?.find((i) => i.id === integrationId);
  return integration?.integrationName ?? '';
}

const isAuthPage = computed(() => route.path === '/login' || route.path === '/register');
const isConnectPage = computed(() => route.path === '/connect');
const isServerPage = computed(() => !!route.meta.serverView);
const isKioskPage = computed(
  () =>
    !!route.meta.panelMode ||
    !!route.meta.playMode ||
    !!route.meta.panelPairing ||
    !!route.meta.panelSetup ||
    route.path === '/pair' ||
    route.path === '/play-pair',
);
const isCloudLandingPage = computed(() => route.path === '/cloud' || (!!route.meta.cloudView && !route.params.hubId));
const useBareLayout = computed(
  () =>
    isAuthPage.value ||
    isConnectPage.value ||
    isKioskPage.value ||
    isCloudLandingPage.value ||
    !!route.meta.bare ||
    route.path === '/tab-connect',
);
/**
 * What the hub-shell rule is asked (card `t_bfe0edc9`), in one place: the facts
 * are the route's, this store's, and the window's own hub record — never the
 * shared localStorage keys, because one tab must not answer for another.
 */
const hubShellFacts = computed(() => {
  const hub = readTabActiveHub();
  const cloudAccessForRoute =
    authStore.isCloudAuthenticated && (route.params.hubId !== undefined || !!route.meta.cloudView);
  const deviceCredential = hub
    ? hasStoredDeviceCredential({ ip: hub.ip, port: hub.port, secure: hub.secure, hubId: hub.hubId ?? null })
    : false;
  return {
    hubPage: !!route.meta.requiresAuth,
    hasCredential: authStore.isAuthenticated || cloudAccessForRoute || deviceCredential,
    hub,
    kiosk: isKioskPage.value,
  };
});
const layout = computed(() => {
  if (isServerPage.value) return ServerLayout;
  if (useBareLayout.value) return 'div';
  // A hub's own pages need a credential behind them (card `t_bfe0edc9`): the
  // measured defect was a hub tab going on painting the hub's shell — its
  // navigation, its dashboards — with nothing behind it, after the route had
  // already decided to fall to /home. `mayRenderHubShell` reads the one fact
  // that matters here (is this a hub page, does this window hold anything that
  // opens the hub it is on) and this is what it paints when there is nothing:
  // the hub named, the fact stated, the two ways on.
  if (!mayRenderHubShell(hubShellFacts.value)) return HubNeedsCredential;
  return DefaultLayout;
});
const mountRuntimeChrome = computed(() => layout.value === DefaultLayout);
// The notification toast is also needed on the Servers page (hub picker) — and
// anywhere else that renders app chrome, i.e. every non-bare layout — **except
// the desktop launcher home** (card t_f0ea3681), which must not paint a balloon,
// play the alert tone or speak: it is the picker, and it holds no hub socket
// (`services/launcherSurface.ts` holds the rule and its test).
const showNotificationChrome = computed(() =>
  mountsNotificationChrome({ desktop: isElectron(), path: route.path, bare: useBareLayout.value }),
);

// ── Electron shell: report this tab's live hub target ─────────────────────
// The shell uses it for the tab's location image, its status dot and to inherit
// the hub when the user opens a built-in app (Player/Panel/...) in a new tab.
// Only available when the tabbed shell created this view
// (`window.electron.reportTabContext`).
const webrtcStore = useWebRTCStore();
const systemStore = useSystemStore();
const systemStatusStore = useSystemStatusStore();
// Read for the shell report below (card `t_92849b4b`): whether this surface
// holds a live hub socket, as opposed to a stored session.
const socketStore = useSocketStore();

// ── The surface this window is on (card t_f0ea3681) ─────────────────────────
// Reported where the launcher rule is spent — the socket store refuses to open
// a hub socket on the desktop launcher home, and the notice layer refuses to
// announce on it. Reported from the app root because the route is the app
// root's (`services/launcherSurface.ts` holds the rule itself). `immediate`, so
// the boot path below (`checkAuth()` in `onMounted`) already sees the real
// route: without it the surface is unknown at the moment the socket is opened.
watch(
  () => route.path,
  (path) => {
    setSurfacePath(path);
    // A window that *becomes* the launcher (a session that dropped, the shell's
    // home button on a hub tab, a fallback redirect) drops the socket it was
    // holding: a launcher holds no hub connection, and one left running would go
    // on receiving notices for a surface that must not announce them.
    if (onDesktopLauncherHome() && socketStore.socket) {
      console.info('[App] Launcher home — dropping this window\'s hub socket');
      socketStore.disconnect();
    }
  },
  { immediate: true },
);

// ── A window that arrived from a notification tap ────────────────────────────
// A service worker can only open a URL, so a browser push lands on
// `/h/<hubId>/<page>?push=1` — and a window with no session for that hub would
// otherwise sit on the Local page waiting to be told. Arriving from a tap is the
// one navigation that should connect by itself: the person already said yes. The
// marker is what tells this apart from an ordinary link to the same route, which
// keeps the router guard's own behaviour (`services/pushDeepLink.ts` owns the
// connect, and it is the same ladder every other connect uses).
watch(
  () => [route.params.hubId, route.query.push] as const,
  () => {
    if (!isPushArrival(route.query)) return;

    const hubId = typeof route.params.hubId === 'string' ? route.params.hubId : '';
    if (!hubId) return;

    // The page inside the hub is whatever is left of the path after the hub.
    const prefix = `/h/${hubId}`;
    const path = route.path.startsWith(prefix) ? route.path.slice(prefix.length) : '';

    void openHubFromPush(path ? { hubId, path } : { hubId }).catch((error) => {
      console.warn('[App] the notification arrival could not be opened:', error);
    });
  },
  { immediate: true },
);

/** Absolute URL of the hub's location image (local sessions only — a cloud
 *  session has no routable hub origin in this renderer). */
function tabLocationImage(ip?: string, port?: number, secure = false): string | undefined {
  if (!ip || !port) return undefined;
  const scheme = secure || port === 443 ? 'https' : 'http';
  const defaultPort = scheme === 'https' ? 443 : 80;
  const portSuffix = port !== defaultPort ? `:${port}` : '';
  return `${scheme}://${ip}${portSuffix}/api/system/settings/image`;
}

function reportTabContext(): void {
  const api = window.electron;
  if (!api?.reportTabContext) return;

  const isCloudSession = webrtcStore.connected;

  // Local target: prefer the persisted active hub (written by setActiveHub on
  // every local login). Parsing config.apiUrl is only a fallback — it can carry
  // a placeholder port (e.g. otto-local://localhost:80/api) before/without a
  // stored address, which would make the shell reconnect to the wrong port.
  // Per-tab first: every Electron tab reports ITS OWN hub to the shell.
  const endpoint = getActiveHubEndpoint();
  let localIp: string | undefined = endpoint?.ip;
  let localPort: number | undefined = endpoint?.port;
  const secure = endpoint?.secure === true;
  if (!localIp || !localPort) {
    const parsed = parseHubApiUrl(getRuntimeConfig().apiUrl);
    if (parsed) {
      localIp = localIp ?? parsed.ip;
      localPort = localPort ?? parsed.port;
    }
  }

  const isLocalSession = authStore.isAuthenticated && !isCloudSession && Boolean(localIp && localPort);
  const connected = isCloudSession || isLocalSession;

  // Highest active problem (status values feed the TopNavigation badge) — the
  // shell draws it as a dot on the tab icon; nothing is reported when all clear.
  const severity = systemStatusStore.highestSeverity;

  // Account button in the bar: always the cloud account — it is the identity
  // that spans hubs, and switching it per hub made the bar lie about who is
  // signed in. Hub users are shown by the hub UI itself (its top-right avatar
  // opens the hub user drawer). A hub avatar is a blob: URL owned by this
  // renderer and cannot be shown in the shell anyway.
  const cloudUser = authStore.cloudUser;
  const account = cloudUser;
  const userName = account ? account.name || account.email || undefined : undefined;
  const rawAvatar = cloudUser?.image;
  const userAvatar = rawAvatar && /^https?:\/\//.test(rawAvatar) ? rawAvatar : undefined;

  void api.reportTabContext({
    connected,
    // Whether this surface holds a **live hub socket** right now (card
    // `t_92849b4b`) — `connected` above is the stored session, which can
    // outlive its socket. The shell spends this: when no tab reports one, the
    // main process drops the socket pool the app's requests ride, so a socket
    // can never outlive the surface that justified it.
    socket: socketStore.connected,
    mode: isCloudSession ? 'cloud' : isLocalSession ? 'local' : undefined,
    ip: isLocalSession ? localIp : undefined,
    port: isLocalSession ? localPort : undefined,
    secure,
    hubId: isCloudSession ? (webrtcStore.hubId ?? undefined) : undefined,
    name: connected ? systemStore.name || undefined : undefined,
    image: isLocalSession ? tabLocationImage(localIp, localPort, secure) : undefined,
    alert: severity === 'error' || severity === 'warning' || severity === 'alert' ? severity : null,
    userName,
    userAvatar,
  });
}

watch(
  () =>
    [
      authStore.isAuthenticated,
      webrtcStore.connected,
      webrtcStore.hubId,
      systemStore.name,
      systemStatusStore.highestSeverity,
      authStore.user,
      authStore.cloudUser,
      // The socket coming up or dropping changes what the shell owns on this
      // surface's behalf (card `t_92849b4b`), so it is reported like the rest.
      socketStore.connected,
    ] as const,
  () => {
    void nextTick(reportTabContext);
  },
  { immediate: true },
);

/**
 * Start native location tracking for a live hub session (card `t_88c3d858`).
 *
 * The tracker's one call site used to live only in the boot path below, and
 * only when boot already found a session. On the cloud relay flow — the
 * phone's normal one — the app boots on the launcher: `checkAuth()` answers
 * false (no local hub token, and Capacitor ships no `apiUrl` until a hub is
 * selected), the boot block is skipped, and the session is opened only
 * AFTERWARDS — so `startTracking()` was never reached for the whole session.
 * The start now follows the session: this one gate is spent by the boot path
 * and by the watcher beside it, so a launcher pick, a LAN claim, a cloud
 * relay connect and a reconnect all end in the tracker starting.
 *
 * Idempotent twice over: this gate only decides *when*, and `startTracking()`
 * itself refuses a second start once its watch is live.
 */
function maybeStartLocationTracking(): void {
  if (
    !shouldStartLocationTracking({
      authenticated: authStore.isAuthenticated,
      webrtcConnected: webrtcStore.connected,
      panelMode: touchPanelStore.isPanelMode,
      mode: getRuntimeConfig().mode,
    })
  ) {
    return;
  }
  void locationTracking.startTracking();
}

// A session that becomes live after the mount starts the tracker here (card
// `t_88c3d858`) — the boot path's own attempt cannot see one. Deliberately not
// `immediate`: the first attempt belongs to the boot path below, made with
// `checkAuth()`'s answer in hand, so a stale stored token cannot start
// tracking before it has been judged.
watch(
  () => [authStore.isAuthenticated, webrtcStore.connected] as const,
  () => {
    maybeStartLocationTracking();
  },
);

onMounted(async () => {
  // Initialize locale from localStorage
  localeStore.initializeLocale();
  await localeStore.setLocale(localeStore.currentLocale);

  // Initialize Capacitor OAuth deep link listener (Android/iOS)
  initOAuthDeepLinks();

  // Global listener for Electron OAuth callbacks (works from any page)
  initGlobalOAuthListener();

  // Phone chrome (card t_6a634e11): one Android hardware-back listener and one
  // edge-swipe listener (touch + pointer). The router they read was handed over
  // in setup, above.
  installMobileBackNavigation();
  installMobileEdgeSwipe();

  // Electron shell: the system bar can ask this tab to navigate (home button
  // → Servers page; it activates the pinned home tab first).
  const electronApi = window.electron;
  if (electronApi?.onNavigate) {
    electronApi.onNavigate((target: string) => {
      if (typeof target === 'string' && target.startsWith('/')) {
        void router.push(target).catch(() => undefined);
      }
    });
  }

  // Electron shell: the bar's account button. It is the cloud account button on
  // every route — the bar shows the cloud identity regardless of the hub the
  // tab is connected to, so its click target must not depend on the route
  // either. The global CloudAccountPanel reacts to this signal.
  if (electronApi?.onOpenUserMenu) {
    electronApi.onOpenUserMenu(() => {
      ui.requestAccountMenu();
    });
  }

  // Background hub-update notifier (Electron): checks on start + hourly and
  // pops a native balloon when a new hub release is available.
  if (isElectron()) {
    useHubUpdateNotifier().start();
  }

  // Start proactive cloud token refresh timer (refreshes ~5 min before expiry)
  scheduleCloudTokenRefresh();

  // Initialize theme
  themeStore.initTheme();

  // Electron shell tabs boot on /tab-connect with their target hub in the
  // query — that route establishes its own session. Running the global boot
  // path in parallel would validate whatever token the (shared) localStorage
  // holds against the wrong hub and initialize stores for it, flashing the
  // wrong hub before the tab's own connection is live.
  const bootingIntoTabConnect =
    route.path === '/tab-connect' && Boolean(route.query.mode || route.query.hubId || route.query.ip);
  if (bootingIntoTabConnect) {
    console.log('[App] Tab-connect boot — auth/data init deferred to the connect flow.');
  } else {
    // Check if user is authenticated. Await this so stale persisted local-hub
    // tokens are rejected before protected stores start loading data.
    const authenticated = await authStore.checkAuth();

    // Schedule proactive local hub token refresh (Electron / local browser / Capacitor).
    if (authenticated) {
      scheduleLocalTokenRefresh();
    }

    // Initialize global data if user is authenticated
    // Skip in panel mode (entities will be loaded after socket registration)
    // Skip in cloud mode (will initialize after connecting to hub)
    if (authenticated && !touchPanelStore.isPanelMode) {
      const config = getRuntimeConfig();

      if (config.mode !== 'cloud') {
        try {
          // Load global entities (devices, rooms, areas, scenes, integrations)
          await entitiesStore.initialize();

          // Load cameras
          const camerasStore = useCamerasStore();
          await camerasStore.initialize();
        } catch (error) {
          console.error('Failed to initialize app data:', error);
          // Non-blocking - app can still function
        }

        // Start location tracking on Capacitor if user consented. This is the
        // boot attempt — a session that opens after the mount starts the
        // tracker from `maybeStartLocationTracking`'s watcher (card t_88c3d858).
        maybeStartLocationTracking();

        // Load favicon settings
        favicon.loadFaviconSettings();
      } else {
        console.log('[App] Skipping data initialization in cloud mode (will load after connecting to hub)');
      }
    }
  }

  // ── Tab visibility handler: detect when user returns after backgrounding ──
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', async () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }

    // Tab just became visible again
    const hiddenDuration = Date.now() - hiddenAt;
    if (hiddenDuration < 30_000) return; // Only act if hidden >30s

    console.log('[App] Tab visible after', Math.round(hiddenDuration / 1000), 's, checking auth');

    try {
      const config = getRuntimeConfig();
      // Cloud session: refresh the access token if it expired while the
      // window was hidden — otherwise cloud features silently break.
      if (authStore.isCloudAuthenticated && authStore.cloudAccessToken) {
        const ok = await authStore.ensureCloudToken();
        if (!ok) {
          // One retry for a transient network blip before giving up.
          await new Promise((r) => {
            setTimeout(r, 2000);
          });
          const retried = await authStore.ensureCloudToken();
          if (!retried) {
            console.warn('[App] Cloud token expired while backgrounded and refresh failed');
            authStore.invalidateCloudSession();
          }
        }
      }
      // In local/electron mode, verify the hub token is still valid and the
      // socket is connected. Refresh the token first when it expired (or is
      // about to) while the window was hidden — checkAuth() would clear it,
      // making a silent refresh impossible.
      if ((config.mode === 'local' || config.mode === 'electron') && authStore.isAuthenticated) {
        if (authStore.isLocalTokenExpiringSoon()) {
          const refreshed = await authStore.refreshLocalHubToken();
          if (!refreshed) {
            console.warn('[App] Token expired while backgrounded and refresh failed');
            await authStore.notifySessionExpired();
            return;
          }
        } else {
          const tokenValid = await authStore.checkAuth();
          if (!tokenValid) {
            console.warn('[App] Token invalid while backgrounded, redirecting');
            await authStore.notifySessionExpired();
            return;
          }
        }
        // If socket is disconnected, try reconnecting. Never on the desktop
        // launcher home (card t_f0ea3681): the picker holds no hub connection,
        // and becoming visible must not open one — the guard is repeated here so
        // the log says what happened instead of the socket store's refusal being
        // the only trace.
        const socketStore = useSocketStore();
        if (onDesktopLauncherHome()) {
          console.info('[App] Visible again on the launcher home — no hub socket is opened here');
        } else if (!socketStore.connected && authStore.accessToken) {
          console.log('[App] Socket disconnected, reconnecting...');
          socketStore.connect(authStore.accessToken);
        }
      }
    } catch (err) {
      console.error('[App] Visibility check failed:', err);
    }
  });
});
</script>
